/**
 * Mobile Detection Utility
 * 
 * Detects mobile devices and provides routing logic for mobile users.
 * Used to redirect mobile users to a separate mobile experience.
 */

/**
 * Check if the current device is mobile
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  // Check user agent for mobile indicators
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
  
  // Check screen width as backup
  const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  const isSmallScreen = screenWidth <= 768;
  
  const isMobileUA = mobileRegex.test(userAgent);
  const result = isMobileUA || isSmallScreen;
  
  return result;
};

/**
 * Check if the current device is a tablet
 * @returns {boolean} True if tablet device
 */
export const isTabletDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  const tabletRegex = /iPad|Android|Tablet|PlayBook|Silk/i;
  const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  
  // Consider tablets as mobile for our purposes
  return tabletRegex.test(userAgent) && screenWidth <= 1024;
};

/**
 * Check if user should be redirected to mobile experience
 * @returns {boolean} True if should redirect to mobile
 */
export const shouldRedirectToMobile = () => {
  // Clear any forceDesktop values that might be preventing mobile detection
  // This helps with cached localStorage values from previous sessions
  try {
    localStorage.removeItem('forceDesktop');
  } catch (e) {
    // localStorage might not be available
  }

  // Check if URL parameter forces mobile
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('mobile') === 'true') {
    return true;
  }

  // More aggressive mobile detection
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|Samsung|LG|HTC|Nexus|Pixel/i;
  
  // Check screen dimensions
  const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  const screenHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  
  // Consider mobile if:
  // 1. User agent indicates mobile
  // 2. Screen width is small (≤768px)
  // 3. Portrait orientation on small screens
  const isMobileUA = mobileRegex.test(userAgent);
  const isSmallScreen = screenWidth <= 768;
  const isPortraitMobile = screenWidth <= 1024 && screenHeight > screenWidth;
  
  const result = isMobileUA || isSmallScreen || isPortraitMobile;
  
  return result;
};

/**
 * Get the mobile route equivalent for a desktop route
 * @param {string} desktopRoute - The desktop route path
 * @returns {string} The mobile route path
 */
export const getMobileRoute = (desktopRoute) => {
  // Handle parametric routes (like news details, events, etc.)
  if (desktopRoute.startsWith('/News/')) {
    // For news details, redirect mobile users to mobile news detail
    const newsId = desktopRoute.split('/News/')[1];
    return `/mobile/news/${newsId}`;
  }
  
  if (desktopRoute.startsWith('/Events/')) {
    // For event details, allow mobile users to view the desktop event detail for now
    return desktopRoute; // Keep the same route until we create mobile event details
  }
  
  const mobileRoutes = {
    '/': '/mobile',
    '/splash': '/mobile/splash',
    '/news': '/mobile/news',
    '/NewsRoom': '/mobile/news',
    '/events': '/mobile/events',
    '/Events': '/mobile/events',
    '/media-centre': '/mobile/media',
    '/MediaCentre': '/mobile/media',
    '/InstitutionalIntelligence': '/mobile/institutional-intelligence',
    '/institutionalintelligence': '/mobile/institutional-intelligence',
    '/policy-regulatory': '/mobile/policy',
    '/PolicyRegulatory': '/mobile/policy',
    '/working-groups': '/mobile/groups',
    '/WorkingGroups': '/mobile/groups',
    '/capability-innovation': '/mobile/innovation',
    '/CapabilityInnovation': '/mobile/innovation',
    '/council-corner': '/mobile/council',
    '/CouncilCorner': '/mobile/council',
    '/etf-book': '/mobile/etf',
    '/etfbook': '/mobile/etf',
    '/profile': '/mobile/profile',
    '/Profile': '/mobile/profile',
    '/daire': '/mobile/daire',
    '/Daire': '/mobile/daire',
    '/search': '/mobile/search',
    '/Search': '/mobile/search'
  };
  
  return mobileRoutes[desktopRoute] || '/mobile';
};

/**
 * Get the desktop route equivalent for a mobile route
 * @param {string} mobileRoute - The mobile route path
 * @returns {string} The desktop route path
 */
export const getDesktopRoute = (mobileRoute) => {
  // Handle parametric mobile routes (like news details)
  if (mobileRoute.startsWith('/mobile/news/')) {
    // For mobile news details, redirect to desktop news detail
    const newsId = mobileRoute.split('/mobile/news/')[1];
    return `/News/${newsId}`;
  }
  
  const desktopRoutes = {
    '/mobile': '/',
    '/mobile/splash': '/splash',
    '/mobile/news': '/NewsRoom',
    '/mobile/events': '/Events',
    '/mobile/media': '/MediaCentre',
    '/mobile/institutional-intelligence': '/InstitutionalIntelligence',
    '/mobile/policy': '/PolicyRegulatory',
    '/mobile/groups': '/WorkingGroups',
    '/mobile/innovation': '/CapabilityInnovation',
    '/mobile/council': '/CouncilCorner',
    '/mobile/etf': '/etfbook',
    '/mobile/profile': '/Profile',
    '/mobile/daire': '/Daire',
    '/mobile/search': '/Search'
  };
  
  return desktopRoutes[mobileRoute] || '/';
};

/**
 * Check if current route is a mobile route
 * @param {string} route - The route to check
 * @returns {boolean} True if mobile route
 */
export const isMobileRoute = (route) => {
  return route.startsWith('/mobile');
};

/**
 * Force desktop experience (bypass mobile detection)
 * Sets a flag in localStorage to force desktop experience
 */
export const forceDesktopExperience = () => {
  localStorage.setItem('forceDesktop', 'true');
};

/**
 * Clear forced desktop experience
 */
export const clearForceDesktop = () => {
  localStorage.removeItem('forceDesktop');
};

/**
 * Check if desktop experience is forced
 * @returns {boolean} True if desktop is forced
 */
export const isDesktopForced = () => {
  // Check URL parameter to override desktop forcing
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('mobile') === 'true') {
    return false; // URL parameter overrides localStorage
  }
  
  return localStorage.getItem('forceDesktop') === 'true';
};

/**
 * Get the appropriate experience based on device and user preference
 * @returns {string} 'mobile' or 'desktop'
 */
export const getPreferredExperience = () => {
  // Mobile users are always forced to mobile experience - no escape!
  return shouldRedirectToMobile() ? 'mobile' : 'desktop';
};

/**
 * Create a mobile URL with parameter override
 * @param {string} path - The path to redirect to
 * @returns {string} URL with mobile=true parameter
 */
export const createMobileUrl = (path = '/mobile') => {
  const baseUrl = window.location.origin;
  return `${baseUrl}${path}?mobile=true`;
}; 